import React, { ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Spinner } from 'reactstrap';
import { debounce } from 'lodash';
import queryString from 'query-string';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import ReactGA from 'react-ga';

import MainLayout, { NoDataTitle } from '../common/Layout';
import ImageList from './ImageList';
import DetailView from './DetailView';

import FindURL from '../common/FindURL';
import config from '../../../lib/config-api.json';

import { tabImageState } from '../../../modules/tabImage';
import { GetSearchImageParamInImageTabType } from '../../../lib/tabImage/type';
import { PostConsumptionBodyType } from '../../../lib/common/type';

const env = process.env.NODE_ENV;
let { apiInfo } = config;

if (env === 'development') {
  apiInfo = config.apiInfo_dev;
}
interface styleType {
  isDetailViewOpen?: boolean;
  isLoadingImage?: boolean;
}

const ImageComponent = styled(MainLayout)`
  display: flex;
  max-width: 99%;
`;

const ImageContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-top: 24px;
  width: 60%;
  ${(props: styleType) => {
    if (props.isDetailViewOpen) {
      return css`
        width: 60%;
      `;
    }
    return css`
      width: 100%;
    `;
  }}
`;

const DetailContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  flex-grow: 1;
  margin-top: 24px;
  width: 40%;
  margin-left: 10px;
  height: ${window.innerHeight}px;
  ${(props: styleType) => {
    if (props.isDetailViewOpen) {
      return css`
        display: flex;
      `;
    }
    return css`
      display: none;
    `;
  }};
`;

const SpinnerBox = styled.div`
  display: flex;
  justify-content: center;
  height: 100px;
  ${(props: styleType) => {
    if (props.isLoadingImage) {
      return css`
        display: flex;
      `;
    }
    return css`
      display: none;
    `;
  }}
`;

const NoDataBox = styled.div`
  margin-left: auto;
  margin-right: auto;
  max-width: 1070px;
  width: 100%;
  margin-top: 20px;
`;

const NoDataSuggestTitle = styled.div`
  font-family: 'NotoKrM';
  font-size: 14px;
  color: #222222;
  margin-bottom: 6px;
  margin-top: 20px;
`;

const NoDataListBox = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
`;
const NoDataSuggestDot = styled.div`
  display: flex;
  width: 4px;
  height: 4px;
  border-radius: 2px;
  margin-right: 6px;
  background-color: #222222;
`;
const NoDataSuggestList = styled.div`
  display: flex;
  font-family: 'NotoKrL';
  font-size: 14px;
  color: #222222;
`;

interface ImageProps extends RouteComponentProps {
  onSetMenuID: (selectedMenuId: number) => void;
  onSetImageData: (photosData: tabImageState['photosData']) => void;
  onSetIsOpen: (isOpen: tabImageState['isOpenStatus']) => void;
  onSetIsDetailViewOpen: (
    isDetailViewOpen: tabImageState['isDetailViewOpen']
  ) => void;
  onSetSelectPhotoData: (
    selectPhotoData: tabImageState['selectPhotoData']
  ) => void;
  onGetSearchImage: (info: GetSearchImageParamInImageTabType) => void;
  onSetAddImage: (addImageData: tabImageState['addImageData']) => void;
  onPostConsumption: (info: PostConsumptionBodyType) => void;
  isOpenStatus: tabImageState['isOpenStatus'];
  photosData: tabImageState['photosData'];
  isDetailViewOpen: tabImageState['isDetailViewOpen'];
  selectPhotoData: tabImageState['selectPhotoData'];
  imageResult: tabImageState['imageResult'];
  addImageData: tabImageState['addImageData'];
  isLoadingImage: tabImageState['isLoadingImage'];
}

function isString(val: string | string[]): val is string {
  return typeof val === 'string';
}

class Image extends React.Component<ImageProps, any> {
  down: boolean;

  imageKey: number;

  constructor(props: ImageProps) {
    super(props);
    this.down = false;
    this.imageKey = 2;
  }

  componentDidMount() {
    ReactGA.ga('send', 'event', `view_search_image_tab`);
    window.addEventListener(
      'scroll',
      debounce((event: Event) => {
        event.preventDefault();

        const ImageComponentHeight = document.getElementById('imageComponent');

        if (ImageComponentHeight) {
          const scrollBottom =
            (window.scrollY || document.documentElement.scrollTop) +
            window.innerHeight;
          const absoluteBottom =
            window.pageYOffset +
            ImageComponentHeight?.getBoundingClientRect()?.bottom;

          if (scrollBottom >= absoluteBottom && !this.down) {
            this.down = true;
            this.addImage();
          }
        }
      }, 500)
    );
  }

  shouldComponentUpdate(nextProps: ImageProps, nextState: any) {
    const { imageResult, onSetAddImage, addImageData } = this.props;
    if (nextProps.imageResult !== imageResult) {
      if (addImageData.length > 0) {
        const tempList = this.rebuildList(nextProps.imageResult);
        if (tempList && tempList.length > 0) {
          const a = [...addImageData, ...tempList];
          onSetAddImage(a);
        }
      } else {
        const tempList = this.rebuildList(nextProps.imageResult);
        if (tempList) {
          onSetAddImage(tempList);
        }
      }
    }
    return true;
  }

  addImage = () => {
    const { onGetSearchImage } = this.props;

    this.down = true;

    const query = queryString.parse(window.location.search);
    let keyword: string | string[] = '';

    if (query.q) {
      if (isString(query.q)) {
        keyword = query.q;
      } else {
        const index = 0;
        keyword = query.q[index];
      }
    }

    setTimeout(() => {
      onGetSearchImage({
        page: this.imageKey,
        page_count: 40,
        targets: 'travel_info,recommended_info,course_info,festival_info',
        query: keyword,
        type: localStorage.getItem('aivory_option_sort') || '0',
      });
      this.down = false;
      this.imageKey += 1;
    }, 100);
  };

  rebuildList = (
    imageResult: tabImageState['imageResult']
  ): tabImageState['addImageData'] | null => {
    const src = apiInfo.api_url_image;
    const sizeIndex = 0;
    const reBuildImageData: {
      id: number;
      url: string;
      src: string;
      width: number;
      height: number;
      alt: string;
      targetId?: number;
      title?: string | undefined;
      desc?: string | undefined;
      source?: string | undefined;
    }[] = [];

    if (imageResult && imageResult.results.length > 0) {
      imageResult.results.forEach((element, index) => {
        const { title } = element;

        let urlInfo: {
          url: string;
          source: string;
        } = { url: '', source: '' };
        let targetId = 0;
        let width = 1;
        let height = 1;

        if (element.travel_info_id) {
          urlInfo = FindURL(element.cot_id, 0);
          targetId = 0;
        } else if (element.recommended_info_id) {
          urlInfo = FindURL(element.cot_id, 1);
          targetId = 1;
        } else if (element.course_info_id) {
          urlInfo = FindURL(element.cot_id, 2);
          targetId = 2;
        }

        if (element.image_meta) {
          const { size } = element.image_meta;
          [width, height] = [size[0], size[1]];
        }

        reBuildImageData.push({
          id: element.id,
          url: urlInfo.url,
          src: `${src}${element.image}`,
          alt: `${element.title}`,
          targetId,
          width,
          height,
          title,
          desc: element.content,
          source: urlInfo.source,
        });
      });
      if (reBuildImageData.length < 3) {
        while (reBuildImageData.length < 10) {
          reBuildImageData.push({
            id: 5,
            url: '',
            width: 50,
            height: 50,
            src: '',
            alt: ``,
            title: '',
            desc: '',
            source: '',
          });
        }
      }
    }

    return reBuildImageData;
  };

  makeList = (): ReactNode[] | null => {
    const {
      onSetIsDetailViewOpen,
      onSetSelectPhotoData,
      onPostConsumption,
      addImageData,
      selectPhotoData,
    } = this.props;
    const resultReactNode: ReactNode[] | null = [];
    resultReactNode.push(
      <ImageList
        photoData={addImageData}
        selectPhotoData={selectPhotoData}
        onSetIsDetailViewOpen={onSetIsDetailViewOpen}
        onSetSelectPhotoData={onSetSelectPhotoData}
        onPostConsumption={onPostConsumption}
        key={0}
      />
    );
    return resultReactNode;
  };

  makeNodataList = (): ReactNode[] | null => {
    const { isLoadingImage, location, imageResult, addImageData } = this.props;

    const query = queryString.parse(location.search);
    let keyword: string | string[] = '';
    if (query.q) {
      if (isString(query.q)) {
        keyword = query.q;
      } else {
        const index = 0;
        keyword = query.q[index];
      }
    }

    const result: ReactNode[] | null = [];
    if (isLoadingImage) {
      result.push(null);
    } else if (imageResult?.results.length === 0 && addImageData.length === 0) {
      result.push(
        <NoDataBox>
          <NoDataTitle>
            <em>&#39;{keyword}&#39;</em>
            <div>와(과) 일치하는 이미지 검색결과가 없습니다.</div>
          </NoDataTitle>

          <NoDataSuggestTitle>제안</NoDataSuggestTitle>
          <NoDataListBox>
            <NoDataSuggestDot />
            <NoDataSuggestList>
              모든 단어의 철자가 정확한지 확인하세요.
            </NoDataSuggestList>
          </NoDataListBox>
          <NoDataListBox>
            <NoDataSuggestDot />
            <NoDataSuggestList>다른 검색어를 사용해 보세요.</NoDataSuggestList>
          </NoDataListBox>
          <NoDataListBox>
            <NoDataSuggestDot />
            <NoDataSuggestList>
              더 일반적인 검색어를 사용해 보세요.
            </NoDataSuggestList>
          </NoDataListBox>
        </NoDataBox>
      );
    } else {
      result.push(null);
    }
    return result;
  };

  render() {
    const {
      isDetailViewOpen,
      selectPhotoData,
      onSetIsDetailViewOpen,
      isLoadingImage,
      addImageData,
    } = this.props;

    return (
      <ImageComponent id="imageComponent">
        <ImageContent isDetailViewOpen={isDetailViewOpen} id="ImageContent">
          {addImageData.length > 0 ? <>{this.makeList()}</> : null}

          {this.makeNodataList()}
          <SpinnerBox isLoadingImage={isLoadingImage}>
            <Spinner color="primary" />
          </SpinnerBox>
        </ImageContent>
        <DetailContent isDetailViewOpen={isDetailViewOpen}>
          <DetailView
            selectPhotoData={selectPhotoData}
            onSetIsDetailViewOpen={onSetIsDetailViewOpen}
          />
        </DetailContent>
      </ImageComponent>
    );
  }
}

export default withRouter(Image);
